import React from "react";

/* ---------- rectangular primitives (no icons) ---------- */
const SectionFrame = ({ title, children, collapsible = true, defaultOpen = true }) => {
  const TitleBar = (
    <div className="flex items-center">
      <span className="text-sm font-semibold text-slate-800">{title}</span>
    </div>
  );

  if (!collapsible) {
    return (
      <div className="border border-slate-200 bg-white rounded-xl p-3.5 space-y-3">
        {TitleBar}
        {children}
      </div>
    );
  }

  return (
    <details open={defaultOpen} className="group border border-slate-200 bg-white rounded-xl">
      <summary className="list-none cursor-pointer px-3.5 py-3 hover:bg-slate-50 select-none rounded-xl">
        <div className="flex items-center justify-between">
          {TitleBar}
          <svg
            className="h-4 w-4 text-slate-600 transition-transform group-open:rotate-180"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </div>
      </summary>
      <div className="px-3.5 pb-3.5">{children}</div>
    </details>
  );
};

const Checkbox = ({ id, label, checked, onChange }) => (
  <label
    htmlFor={id}
    className="flex items-center gap-2 rounded-lg px-2.5 py-2 hover:bg-slate-50 cursor-pointer"
  >
    <input
      id={id}
      type="checkbox"
      className="h-4 w-4 rounded-sm border-slate-300 text-slate-900 focus:ring-slate-900/20"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="text-sm text-slate-800">{label}</span>
  </label>
);

const RectChip = ({ active, children, onToggle, ariaLabel }) => (
  <button
    type="button"
    aria-pressed={active}
    aria-label={ariaLabel}
    onClick={onToggle}
    className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm border transition
      ${
        active
          ? "bg-black text-white border-black"
          : "bg-white text-slate-800 border-slate-300 hover:bg-slate-50"
      }`}
  >
    {children}
    {active && <span className="ml-1 text-xs opacity-90">✕</span>}
  </button>
);

const Select = ({ label, value, onChange, options, id }) => (
  <label className="block">
    <div className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
      {label}
    </div>
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1.5 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20"
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  </label>
);

/* ---------- main ---------- */
// selected.rating: number[]  (treat as "at least min(thresholds)" in results;
// unrated vehicles should always be kept in results).
export default function Filters({
  options,
  selected,
  onChange,
  sort,
  setSort,
  layout, // "bar" for mobile bar usage
}) {
  const isBar = layout === "bar";

  // In "bar" we buffer state until Apply. On desktop we update immediately.
  const [draft, setDraft] = React.useState(selected);
  const [searchTerm, setSearchTerm] = React.useState("");

  React.useEffect(() => {
    if (!isBar) return;
    setDraft(selected);
  }, [selected, isBar]);

  const state = isBar ? draft : selected;
  const setState = (patch) => {
    const next = { ...state, ...patch };
    if (isBar) setDraft(next);
    else onChange(next);
  };

  const toggleInArray = (key, val) => {
    const set = new Set(state[key] || []);
    if (set.has(val)) set.delete(val);
    else set.add(val);
    setState({ [key]: Array.from(set) });
  };

  const clearAll = () => {
    const cleared = {
      brandModel: [],
      fuelType: [],
      seatingCapacity: [],
      color: [],
      fareRange: [],
      rating: [], // 🔹 clear rating as well
    };
    if (isBar) setDraft(cleared);
    else onChange(cleared);
  };

  const applyDraft = () => isBar && onChange(draft);

  const filteredBrandModel = React.useMemo(() => {
    const list = options.brandModel || [];
    const term = searchTerm.trim().toLowerCase();
    if (!term) return list;
    return list.filter((x) => x.toLowerCase().includes(term));
  }, [options.brandModel, searchTerm]);

  const hasRatingOptions = (options.ratingThresholds || []).length > 0;

  /* ---------- blocks (rectangular, page-scroll only) ---------- */
  const BrandModel = (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          inputMode="search"
          placeholder="Search brand/model…"
          className="flex-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          aria-label="Filter brand and model list"
        />
      </div>

      {/* No internal scrollbar: render full list; page scroll handles overflow */}
      <ul className="mt-1 divide-y divide-slate-100 border border-slate-200 rounded-lg">
        {(filteredBrandModel.length ? filteredBrandModel : ["— No matches —"]).map((bm) => {
          if (bm === "— No matches —") {
            return (
              <li key="none" className="px-3 py-2 text-sm text-slate-500">
                {bm}
              </li>
            );
          }
          const id = `bm-${bm}`;
          const checked = state.brandModel.includes(bm);
          return (
            <li key={bm}>
              <Checkbox
                id={id}
                label={bm}
                checked={checked}
                onChange={() => toggleInArray("brandModel", bm)}
              />
            </li>
          );
        })}
      </ul>

      {/* 🔸 Removed brand/model RectChips – no duplicate render of selected labels */}
    </div>
  );

  const FuelType = (
    <div className="grid grid-cols-1 gap-1">
      {(options.fuelType || []).map((ft) => {
        const id = `fuel-${ft}`;
        const checked = state.fuelType.includes(ft);
        return (
          <Checkbox
            key={ft}
            id={id}
            label={ft}
            checked={checked}
            onChange={() => toggleInArray("fuelType", ft)}
          />
        );
      })}
    </div>
  );

  const Seating = (
    <div className="flex flex-wrap gap-2">
      {(options.seatingCapacity || []).map((s) => {
        const active = state.seatingCapacity.includes(s);
        return (
          <RectChip
            key={s}
            active={active}
            onToggle={() => toggleInArray("seatingCapacity", s)}
            ariaLabel={`Toggle ${s} seats`}
          >
            {s} seats
          </RectChip>
        );
      })}
    </div>
  );

  const Colors = (
    <div className="flex flex-wrap gap-2">
      {(options.color || []).map((c) => {
        const active = state.color.includes(c);
        return (
          <RectChip
            key={c}
            active={active}
            onToggle={() => toggleInArray("color", c)}
            ariaLabel={`Toggle color ${c}`}
          >
            {c}
          </RectChip>
        );
      })}
    </div>
  );

  const Fare = (
    <div className="grid grid-cols-1 gap-1">
      {(options.fareRanges || []).map((fr) => {
        const id = `fare-${fr}`;
        const checked = state.fareRange.includes(fr);
        return (
          <Checkbox
            key={fr}
            id={id}
            label={fr}
            checked={checked}
            onChange={() => toggleInArray("fareRange", fr)}
          />
        );
      })}
    </div>
  );

  // 🔹 Rating filter – smooth rectangular chips (4.0+, 3.0+, etc.)
  const Rating = hasRatingOptions ? (
    <div className="flex flex-wrap gap-2">
      {(options.ratingThresholds || []).map((r) => {
        const active = (state.rating || []).includes(r);
        return (
          <RectChip
            key={r}
            active={active}
            onToggle={() => toggleInArray("rating", r)}
            ariaLabel={`Toggle minimum rating ${r} stars`}
          >
            <span className="tabular-nums">{r.toFixed ? r.toFixed(1) : r}</span>
            <span>★ &amp; up</span>
          </RectChip>
        );
      })}
    </div>
  ) : null;

  const Sort = (
    <Select
      label="Sort by"
      id="sort"
      value={sort}
      onChange={setSort}
      options={[
        { value: "fare-asc", label: "Fare: Low to High" },
        { value: "fare-desc", label: "Fare: High to Low" },
      ]}
    />
  );

  /* ---------- layouts ---------- */
  if (isBar) {
    // Compact mobile "bar" mode (no internal scroll components)
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <SectionFrame title="Brand & model" defaultOpen={false}>
            {BrandModel}
          </SectionFrame>
          <SectionFrame title="Fuel type" defaultOpen={false}>
            {FuelType}
          </SectionFrame>
          <SectionFrame title="Seating" defaultOpen={false}>
            {Seating}
          </SectionFrame>
          <SectionFrame title="Color" defaultOpen={false}>
            {Colors}
          </SectionFrame>
          <SectionFrame title="Fare range" defaultOpen={false}>
            {Fare}
          </SectionFrame>
          {hasRatingOptions && (
            <SectionFrame title="Rating" defaultOpen={false}>
              {Rating}
            </SectionFrame>
          )}
          <SectionFrame title="Sort" collapsible={false}>
            {Sort}
          </SectionFrame>
        </div>

        <div className="flex items-center justify-between gap-3 pt-1">
          <button
            type="button"
            onClick={clearAll}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium hover:bg-slate-50"
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={applyDraft}
            className="rounded-lg bg-black px-5 py-2 text-sm font-semibold text-white shadow-sm active:scale-[0.98]"
          >
            Apply
          </button>
        </div>
      </div>
    );
  }

  // Desktop panel (applies immediately)
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold text-slate-800">Filters</div>
        <button
          type="button"
          onClick={clearAll}
          className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium hover:bg-slate-50"
        >
          Clear all
        </button>
      </div>

      <SectionFrame title="Brand & model">{BrandModel}</SectionFrame>
      <SectionFrame title="Fuel type">{FuelType}</SectionFrame>
      <SectionFrame title="Seating">{Seating}</SectionFrame>
      <SectionFrame title="Color">{Colors}</SectionFrame>
      <SectionFrame title="Fare range">{Fare}</SectionFrame>
      {hasRatingOptions && <SectionFrame title="Rating">{Rating}</SectionFrame>}
      <SectionFrame title="Sort" collapsible={false}>
        {Sort}
      </SectionFrame>
    </div>
  );
}
